// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import apiServices from '../services/apiService';
import './Login.css';

const Login = ({ onLogin }) => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setUserId('');
    setPassword('');
    setErrorMessage('');
  }, []);

  const showToast = (message, type = 'info') => {
    const toast = document.createElement('div');
    toast.className = `genops-toast genops-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const loginResult = await apiServices.login(userId, password);

      if (loginResult.success) {
        showToast('Login successful!', 'success');

        if (typeof onLogin === 'function') {
          onLogin(loginResult.data);
        }
        
        setLoading(false);
      } else {
        setLoading(false);
        const errorMsg = loginResult.message || "Invalid credentials.";
        setErrorMessage(errorMsg);
        showToast(errorMsg, 'error');
      }
    } catch (err) {
      console.error("Login Error:", err);
      setLoading(false);
      const errorMsg = "An unexpected error occurred. Please try again.";
      setErrorMessage(errorMsg);
      showToast(errorMsg, 'error');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!userId.trim() || !password.trim()) {
      const errorMsg = 'Both Employee ID and Password are required.';
      setErrorMessage(errorMsg);
      showToast(errorMsg, 'error');
      return;
    }

    handleLogin();
  };

  return (
    <div className="genops-login-container">
      <div className="genops-login-wrapper">
        {/* Left Side - Image Section */}
        <div className="genops-image-section">
          {!imageError ? (
            <img 
              src={`${process.env.PUBLIC_URL}/genus-main1.jpg`}
              alt="Genus Manufacturing"
              className="genops-main-image"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="genops-image-fallback" />
          )}
        </div>

        {/* Right Side - Login Form */}
        <div className="genops-form-section">
          {/* Welcome Section - UPDATED */}
          <div className="genops-welcome-section">
            <div className="genops-brand-container">
              <span className="genops-brand-name">GenOps</span>
            </div>
            <p className="genops-tagline">Complete Visibility Across Production</p>
            <div className="genops-divider"></div>
            <p className="genops-signin-text">Monitor. Optimize. Excel.</p>
            <p className="genops-signin-subtext">Sign in to continue</p>
          </div>

          {errorMessage && (
            <div className="genops-error-message">{errorMessage}</div>
          )}

          <form onSubmit={handleSubmit} className="genops-form">
            <div className="genops-form-group">
              <label className="genops-label">Employee ID</label>
              <div className="genops-input-wrapper">
                <span className="genops-input-icon">👤</span>
                <input
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter your Employee Code"
                  className={`genops-input ${errorMessage ? 'error' : ''}`}
                  required
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="genops-form-group">
              <label className="genops-label">Password</label>
              <div className="genops-input-wrapper">
                <span className="genops-input-icon">🔒</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className={`genops-input ${errorMessage ? 'error' : ''}`}
                  required
                  disabled={loading}
                />
                <button 
                  type="button" 
                  className="genops-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Toggle password visibility"
                  disabled={loading}
                >
                  {showPassword ? (
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="genops-login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="genops-spinner"></span>
                  Logging in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="genops-footer">
            Copyright Genus © 2026
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;